import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import apiClient from "../../api/apiClient";
import { setEmailVerified } from "../../store/UserSlice";
import { RootState } from "../../store/store";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { ArrowLeft } from "heroicons-react";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { token: routeToken } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const token = routeToken || searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const user = useSelector((state: RootState) => state.user.user);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setErrorMessage(
        t(
          "verification.missingToken",
          "No verification token provided. Please click the link in your verification email."
        )
      );
      return;
    }

    verifyToken(token);
  }, [token]);

  const verifyToken = async (verificationToken: string) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const res = await apiClient.get(
        `/api/v1/auth/verify-email/${verificationToken}`
      );

      if (res.data?.status || res.status === 200) {
        setSuccess(true);
        dispatch(setEmailVerified(true));
        toast.success(
          res.data?.message ||
            t("verification.successToast", "Email verified successfully!")
        );
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      const msg =
        error.response?.data?.error?.message ||
        t(
          "verification.failedMessage",
          "Verification link is invalid or has expired (48-hour limit)."
        );
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailToUse = resendEmail.trim() || user?.user?.email || user?.email;

    if (!emailToUse || !emailToUse.includes("@")) {
      toast.warn(
        t("verification.enterValidEmail", "Please enter a valid email address.")
      );
      return;
    }

    try {
      setResending(true);
      const res = await apiClient.post("/api/v1/auth/resend-verification", {
        email: emailToUse,
      });

      setResendSuccess(true);
      toast.success(
        res.data?.message ||
          t(
            "verification.resendSuccess",
            "Verification email sent! Please check your inbox and spam folder."
          )
      );
    } catch (error: any) {
      console.error("Resend error:", error);
      toast.error(
        error.response?.data?.error?.message ||
          t("verification.resendError", "Failed to resend verification email.")
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen lg:h-screen lg:overflow-hidden bg-[#F9FAFC]">
      {/* Left Image Banner */}
      <div className="hidden lg:flex w-1/2 h-full">
        <img
          src="./images/loginpic.jpg"
          className="object-cover w-full h-full"
          alt="sideimage"
        />
      </div>

      {/* Right Content Section */}
      <div className="w-full lg:w-1/2 px-6 sm:px-12 py-4 flex flex-col justify-between h-full overflow-y-auto">
        {/* Top bar */}
        <div className="w-full flex items-center justify-between pb-2 shrink-0">
          <Link
            to="/"
            className="text-[#037897] font-gilroy-medium text-sm flex items-center hover:opacity-80 transition-opacity"
          >
            <ArrowLeft className="w-4 inline-block mr-2" />
            {t("login.backHome", "Back to Home")}
          </Link>
          <LanguageSwitcher isMobile={true} />
        </div>

        {/* Card Content */}
        <div className="w-full max-w-lg mx-auto my-auto py-6">
          <div className="text-center mb-6">
            <Link to="/" className="inline-block">
              <img
                src="./images/bluelogo-new.png"
                alt="Logo"
                className="mx-auto mb-3 max-w-[130px] md:max-w-[150px]"
              />
            </Link>
          </div>

          <div className="bg-white rounded-3xl p-8 sm:p-10 border border-gray-100 shadow-sm text-center">
            {loading ? (
              <div className="py-8 flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#037897] border-t-transparent rounded-full animate-spin mb-4" />
                <h3 className="text-lg font-bold text-[#041B44]">
                  {t("verification.verifyingTitle", "Verifying your email...")}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {t("verification.verifyingDesc", "Please wait a moment while we validate your link.")}
                </p>
              </div>
            ) : success ? (
              <div className="py-4">
                <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-4 border border-emerald-200">
                  ✓
                </div>
                <h2 className="text-2xl font-bold text-[#041B44]">
                  {t("verification.verifiedSuccessTitle", "Email Verified Successfully!")}
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mt-2 leading-relaxed max-w-md mx-auto">
                  {t(
                    "verification.verifiedSuccessDesc",
                    "Thank you for confirming your email address. Your Centre SEVA account is fully activated."
                  )}
                </p>

                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  {isLoggedIn ? (
                    <Link
                      to="/bookings"
                      className="w-full sm:w-auto bg-[#037897] hover:bg-[#025c74] text-white px-8 py-3 rounded-xl font-bold text-sm shadow-xs transition-colors text-center inline-block"
                    >
                      {t("verification.goToDashboard", "Go to My Dashboard")} &rarr;
                    </Link>
                  ) : (
                    <Link
                      to="/login"
                      className="w-full sm:w-auto bg-[#037897] hover:bg-[#025c74] text-white px-8 py-3 rounded-xl font-bold text-sm shadow-xs transition-colors text-center inline-block"
                    >
                      {t("verification.proceedLogin", "Proceed to Login")} &rarr;
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              <div className="py-2 text-left">
                <div className="w-16 h-16 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-4 border border-amber-200">
                  ⚠️
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#041B44] text-center">
                  {t("verification.expiredTitle", "Verification Link Expired")}
                </h2>
                <p className="text-sm text-gray-600 mt-2 text-center leading-relaxed">
                  {errorMessage ||
                    t(
                      "verification.expiredDesc",
                      "This link is invalid or has expired. Verification links are valid for 48 hours."
                    )}
                </p>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h4 className="text-sm font-bold text-[#041B44] mb-1">
                    {t("verification.requestNewLink", "Request a new verification link")}
                  </h4>
                  <p className="text-xs text-gray-500 mb-4">
                    {t(
                      "verification.requestNewLinkDesc",
                      "Enter your registered email address and we'll send a fresh 48-hour link."
                    )}
                  </p>

                  {resendSuccess ? (
                    <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 rounded-xl text-xs sm:text-sm">
                      ✅ {t("verification.resendSuccessNotice", "A new verification email has been dispatched. Please check your inbox!")}
                    </div>
                  ) : (
                    <form onSubmit={handleResend} className="flex flex-col gap-3">
                      <input
                        type="email"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        placeholder={t("verification.emailPlaceholder", "Enter your account email...")}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-hidden focus:border-[#037897] focus:ring-1 focus:ring-[#037897]"
                        required
                      />
                      <button
                        type="submit"
                        disabled={resending}
                        className="bg-[#037897] hover:bg-[#025c74] text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-xs transition-colors disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
                      >
                        {resending ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>{t("verification.sending", "Sending...")}</span>
                          </>
                        ) : (
                          <>
                            <span>✉️</span>
                            <span>{t("verification.resendButtonAction", "Send Verification Link")}</span>
                          </>
                        )}
                      </button>
                    </form>
                  )}

                  <div className="text-center mt-6">
                    <Link
                      to="/login"
                      className="text-xs text-[#037897] font-semibold hover:underline"
                    >
                      {t("verification.backToLogin", "Return to Login")}
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="text-center text-xs text-gray-400 py-2 shrink-0">
          Centre SEVA &bull; {t("verification.portalSecurity", "Secure Healthcare Portal")}
        </div>
      </div>
    </div>
  );
}
